import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { ChatInterface } from './ChatInterface'
import { ConversationSidebar } from './ConversationSidebar'
import { ContactList } from './ContactList'
import { SettingsPanel } from './SettingsPanel'
import { ProfilePanel } from './ProfilePanel'
import { ContactDetail } from './ContactDetail'
import { BotConfigModal } from './BotConfigModal'
import { LlmBotEditor } from './LlmBotEditor'
import { ConfirmDialog } from './ConfirmDialog'
import { MYAGENT_PARTICIPANT_ID, MYAGENT_PARTICIPANT } from './participantColors.js'
import { usePeerService } from '../hooks/usePeerService.js'
import './AppLayout.css'

const ChatsIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
)

const ContactsIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
    <path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
)

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
  </svg>
)

const NAV_ITEMS = [
  { id: 'chats', Icon: ChatsIcon, title: 'Chats' },
  { id: 'contacts', Icon: ContactsIcon, title: 'Contacts' },
  { id: 'settings', Icon: SettingsIcon, title: 'Settings' },
]

const PANEL_TITLES = {
  chats: 'Chats',
  contacts: 'Contacts',
  settings: 'Settings',
  profile: 'Profile',
}

export const AppLayout = ({
  api, onAuthError, onLogout,
  userInfo, llmConfig, sessionConfig,
  peerService, roleCall,
  emptyMessage = 'How can I help you today?',
  placeholder = 'Message MyAgent...',
}) => {
  // Navigation
  const [activeTab, setActiveTab] = useState(() => {
    const h = location.hash.slice(1)
    if (h.startsWith('conv/')) return 'chats'
    return ['contacts', 'settings', 'profile'].includes(h) ? h : 'chats'
  })
  const [showSidebar, setShowSidebar] = useState(() =>
    !(location.hash.startsWith('#conv/') && window.innerWidth <= 768)
  )

  // Conversation state
  const [activeConversationId, setActiveConversationId] = useState(() =>
    location.hash.startsWith('#conv/') ? location.hash.slice(6) : null
  )
  const [conversationIndex, setConversationIndex] = useState([])

  // Contacts/participants
  const [participants, setParticipants] = useState([])
  const participantsRef = useRef(participants)
  participantsRef.current = participants

  // Contact detail
  const [selectedContact, setSelectedContact] = useState(null)

  // Modals
  const [configBot, setConfigBot] = useState(null)
  const [showClearDialog, setShowClearDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Peers (unified contacts: LLM bots + worker roles)
  const { peers, bots: llmBots, refresh: refreshPeers } = usePeerService(peerService)

  const [editingBot, setEditingBot] = useState(undefined) // undefined=hidden, null=new, object=editing

  // LLM bot CRUD handlers
  const handleSaveBot = useCallback(async (config) => {
    if (!peerService) return
    if (config.botId) {
      await peerService.updateBot(config.botId, config)
    } else {
      await peerService.addBot(config)
    }
    await refreshPeers()
    setEditingBot(undefined)
  }, [peerService, refreshPeers])

  const handleDeleteBot = useCallback(async (botId) => {
    if (!peerService) return
    await peerService.removePeer(botId)
    await refreshPeers()
    setEditingBot(undefined)
    if (selectedContact?.roleId === botId) setSelectedContact(null)
  }, [peerService, refreshPeers, selectedContact])

  // URL hash sync
  useEffect(() => {
    let newHash = ''
    if (activeTab === 'chats' && activeConversationId) newHash = `#conv/${activeConversationId}`
    else if (activeTab !== 'chats') newHash = `#${activeTab}`
    if (location.hash !== newHash) history.replaceState(null, '', newHash || location.pathname)
  }, [activeTab, activeConversationId])

  // Derive conversation target
  const activeConv = activeConversationId
    ? conversationIndex.find(c => c.id === activeConversationId)
    : null
  const conversationTarget = useMemo(() => {
    if (!activeConv) return null
    // Check peers first (covers both LLM bots and workers)
    const peer = peers.find(p => p.roleId === activeConv.targetRoleId)
    if (peer) return { roleId: peer.roleId, displayName: peer.displayName, type: peer.type, online: peer.online }
    const p = participants.find(p => p.roleId === activeConv.targetRoleId)
    return p
      ? { roleId: p.roleId, displayName: p.displayName, type: 'worker', online: false }
      : { roleId: activeConv.targetRoleId, displayName: activeConv.title || 'Bot', type: 'worker', online: false }
  }, [activeConv, participants, peers])

  // Resolve active conversation's bot config (for LLM bot conversations)
  const activeBotConfig = useMemo(() => {
    if (!activeConv) return null
    const bot = llmBots.find(b => b.botId === activeConv.targetRoleId)
    return bot || null
  }, [activeConv, llmBots])

  // Load conversation index
  useEffect(() => {
    if (!api) return
    api.getConversationIndex().then(index => {
      setConversationIndex(index)
      const hashId = location.hash.startsWith('#conv/') ? location.hash.slice(6) : null
      if (hashId && !index.find(c => c.id === hashId)) setActiveConversationId(null)
    }).catch(() => {})
  }, [api])

  // --- Participant management ---
  const ensureMyAgent = useCallback((list) => {
    if (list.some(p => p.roleId === MYAGENT_PARTICIPANT_ID)) return list
    return [MYAGENT_PARTICIPANT, ...list]
  }, [])

  const syncParticipants = useCallback(async (stored, roles) => {
    if (!stored?.length || !roles?.length) return
    const roleMap = new Map(roles.map(r => [r.roleId, r]))
    let changed = false
    const nameChanges = new Map()
    const updated = stored.map(p => {
      const fresh = roleMap.get(p.roleId)
      if (fresh && (fresh.displayName !== p.displayName || fresh.typeName !== p.typeName || fresh.roleType !== p.roleType)) {
        changed = true
        if (fresh.displayName !== p.displayName) nameChanges.set(p.roleId, fresh.displayName)
        return { ...p, displayName: fresh.displayName, typeName: fresh.typeName, description: fresh.description, roleType: fresh.roleType }
      }
      return p
    })
    if (changed) {
      setParticipants(updated)
      await api.setParticipants(updated)
      if (nameChanges.size > 0) {
        setConversationIndex(prev => prev.map(c =>
          nameChanges.has(c.targetRoleId) ? { ...c, title: nameChanges.get(c.targetRoleId) } : c
        ))
      }
    }
  }, [api])

  // Load participants on mount
  useEffect(() => {
    if (!api) return
    api.getParticipants().then(stored => setParticipants(ensureMyAgent(stored || []))).catch(() => {})
  }, [api])

  // Sync participants with worker roles when they change
  const participantsInitialized = useRef(false)
  useEffect(() => {
    const workerPeers = peers.filter(p => p.type === 'worker')
    if (!api || workerPeers.length === 0) return
    // Auto-populate on first load if participants empty (only myagent)
    if (!participantsInitialized.current) {
      participantsInitialized.current = true
      if (participantsRef.current.length <= 1) {
        const onlineRoles = workerPeers.filter(r => r.online)
        if (onlineRoles.length > 0) {
          const populated = ensureMyAgent(onlineRoles)
          setParticipants(populated)
          api.setParticipants(populated)
        }
      }
    }
    syncParticipants(participantsRef.current, workerPeers)
  }, [api, peers])

  const addParticipant = useCallback(async (role) => {
    if (participants.some(p => p.roleId === role.roleId)) return
    const updated = [...participants, role]
    setParticipants(updated)
    await api.setParticipants(updated)
  }, [api, participants])

  const removeParticipant = useCallback(async (roleId) => {
    if (roleId === MYAGENT_PARTICIPANT_ID) return
    const updated = participants.filter(p => p.roleId !== roleId)
    setParticipants(updated)
    await api.setParticipants(updated)
  }, [api, participants])

  const handleDeleteRole = useCallback(async (roleId) => {
    if (!peerService) return
    await peerService.removePeer(roleId)
    await refreshPeers()
    setSelectedContact(prev => prev?.roleId === roleId ? null : prev)
    const updated = participants.filter(p => p.roleId !== roleId)
    if (updated.length !== participants.length) {
      setParticipants(updated)
      await api.setParticipants(updated)
    }
  }, [peerService, refreshPeers, participants, api])

  // --- Conversation handlers ---
  const handleNewConversation = useCallback(async (participant) => {
    const conv = await api.createConversation({ targetRoleId: participant.roleId, title: participant.displayName })
    setConversationIndex(prev => [...prev, conv])
    setActiveConversationId(conv.id)
    setActiveTab('chats')
    setShowSidebar(false)
  }, [api])

  const handleSwitchConversation = useCallback((convId) => {
    setActiveConversationId(convId)
    setShowSidebar(false)
  }, [])

  const handleDeleteConversation = useCallback(async (convId) => {
    await api.deleteConversation(convId)
    setConversationIndex(prev => prev.filter(c => c.id !== convId))
    if (activeConversationId === convId) setActiveConversationId(null)
  }, [api, activeConversationId])

  const handleClearChat = useCallback(() => {
    setShowClearDialog(true)
  }, [])

  const confirmClearChat = async () => {
    try {
      if (activeConversationId) {
        await api.deleteConversation(activeConversationId)
        setConversationIndex(prev => prev.filter(c => c.id !== activeConversationId))
        setActiveConversationId(null)
      } else {
        await api.clearMainChat()
        window.location.reload()
      }
    } catch (error) {
      setErrorMessage(`Failed to clear chat: ${error.message}`)
    }
  }

  const handleBack = useCallback(() => {
    setShowSidebar(true)
  }, [])

  const handleStartChat = useCallback(async (contact) => {
    const existing = conversationIndex.find(c => c.targetRoleId === contact.roleId)
    if (existing) {
      setActiveConversationId(existing.id)
    } else {
      const conv = await api.createConversation({ targetRoleId: contact.roleId, title: contact.displayName })
      setConversationIndex(prev => [...prev, conv])
      setActiveConversationId(conv.id)
    }
    setActiveTab('chats')
    setSelectedContact(null)
    setShowSidebar(false)
  }, [api, conversationIndex])

  const chatTitle = activeConv ? (activeConv.title || 'New conversation') : 'MyAgent'
  const inputPlaceholder = conversationTarget
    ? `Message ${conversationTarget.displayName}...`
    : placeholder

  // User avatar for nav rail
  const avatarInitial = (() => {
    const name = userInfo?.name || userInfo?.email?.split('@')[0] || ''
    return name.charAt(0).toUpperCase() || '?'
  })()

  return (
    <div className="app-layout">
      <BotConfigModal
        isOpen={!!configBot}
        onClose={() => setConfigBot(null)}
        roleId={configBot?.roleId}
        name={configBot?.displayName}
        peerService={peerService}
      />

      <ConfirmDialog
        isOpen={showClearDialog}
        onClose={() => setShowClearDialog(false)}
        onConfirm={confirmClearChat}
        title={activeConversationId ? 'Delete Conversation' : 'Clear Chat'}
        message={activeConversationId ? 'Delete this conversation and all its messages?' : 'Clear all messages in the main chat?'}
        confirmText={activeConversationId ? 'Delete' : 'Clear'}
        cancelText="Cancel"
        variant="danger"
      />

      {errorMessage && (
        <ConfirmDialog
          isOpen={!!errorMessage}
          onClose={() => setErrorMessage('')}
          onConfirm={() => setErrorMessage('')}
          title="Error"
          message={errorMessage}
          confirmText="OK"
          cancelText=""
          variant="danger"
        />
      )}

      {/* Column 1: Nav Rail */}
      <nav className="nav-rail">
        <div className="nav-rail-top">
          {NAV_ITEMS.map(({ id, Icon, title }) => (
            <button
              key={id}
              className={`nav-btn${activeTab === id ? ' nav-btn-active' : ''}`}
              onClick={() => {
                setActiveTab(id)
                if (id === 'chats' || id === 'contacts') setShowSidebar(true)
                else setShowSidebar(false)
                if (id !== 'contacts') setSelectedContact(null)
              }}
              title={title}
            >
              <Icon />
            </button>
          ))}
        </div>
        <div className="nav-rail-bottom">
          {userInfo?.avatar ? (
            <img
              src={userInfo.avatar}
              alt={userInfo.name || 'User'}
              className={`nav-avatar${activeTab === 'profile' ? ' nav-avatar-active' : ''}`}
              referrerPolicy="no-referrer"
              onClick={() => { setActiveTab('profile'); setShowSidebar(false); setSelectedContact(null) }}
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
            />
          ) : null}
          <div
            className={`nav-avatar-initials${activeTab === 'profile' ? ' nav-avatar-active' : ''}`}
            style={userInfo?.avatar ? { display: 'none' } : undefined}
            onClick={() => { setActiveTab('profile'); setShowSidebar(false); setSelectedContact(null) }}
          >
            {avatarInitial}
          </div>
        </div>
      </nav>

      {/* Column 2: List Panel (chats & contacts only) */}
      {(activeTab === 'chats' || activeTab === 'contacts') && (
        <div className={`list-panel${showSidebar ? '' : ' list-panel-hidden'}`}>
          <div className="list-panel-header">
            <h2 className="list-panel-title">{PANEL_TITLES[activeTab]}</h2>
          </div>
          <div className="list-panel-content">
            {activeTab === 'chats' && (
              <ConversationSidebar
                conversations={conversationIndex}
                activeConversationId={activeConversationId}
                onSelect={handleSwitchConversation}
                onDelete={handleDeleteConversation}
                onCreate={handleNewConversation}
                onClear={handleClearChat}
                contacts={peers}
              />
            )}
            {activeTab === 'contacts' && (
              <>
                {peerService && (
                  <button className="new-bot-btn" onClick={() => { setEditingBot(null); setSelectedContact(null) }}>
                    + New LLM Bot
                  </button>
                )}
                <ContactList
                  contacts={peers}
                  onSelectContact={(c) => { setSelectedContact(c); setEditingBot(undefined) }}
                  selectedContactId={selectedContact?.roleId}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Column 3: Main Content */}
      <div className={`app-main${showSidebar && (activeTab === 'chats' || activeTab === 'contacts') ? ' app-main-hidden' : ''}`}>
        {activeTab === 'chats' && (
          <ChatInterface
            api={api}
            onAuthError={onAuthError}
            peerService={peerService}
            roleCall={roleCall}
            activeConversationId={activeConversationId}
            conversationTarget={conversationTarget}
            botConfig={activeBotConfig}
            participants={participants}
            contacts={peers}
            emptyMessage={emptyMessage}
            placeholder={inputPlaceholder}
            chatTitle={chatTitle}
            onBack={handleBack}
            onParticipantsChanged={setParticipants}
            onAddParticipant={addParticipant}
            onRemoveParticipant={removeParticipant}
            onRefreshContacts={refreshPeers}
            onConfigureBot={setConfigBot}
          />
        )}
        {activeTab === 'contacts' && editingBot !== undefined && (
          <LlmBotEditor
            bot={editingBot}
            onSave={handleSaveBot}
            onCancel={() => setEditingBot(undefined)}
            onDelete={editingBot?.isBuiltIn ? null : handleDeleteBot}
          />
        )}
        {activeTab === 'contacts' && editingBot === undefined && selectedContact && (
          selectedContact.type === 'llm-bot' ? (
            <LlmBotEditor
              bot={selectedContact.botConfig}
              onSave={handleSaveBot}
              onCancel={() => setSelectedContact(null)}
              onDelete={handleDeleteBot}
              onStartChat={(bot) => handleStartChat({ roleId: bot.botId, displayName: bot.displayName })}
            />
          ) : (
            <ContactDetail
              contact={selectedContact}
              isOnline={selectedContact.online}
              peerService={peerService}
              onStartChat={handleStartChat}
              onDeleteRole={handleDeleteRole}
            />
          )
        )}
        {activeTab === 'settings' && llmConfig && (
          <SettingsPanel llmConfig={llmConfig} sessionConfig={sessionConfig} />
        )}
        {activeTab === 'profile' && (
          <ProfilePanel userInfo={userInfo} onLogout={onLogout} />
        )}
      </div>
    </div>
  )
}
